import { useCallback, useEffect, useRef, useState } from 'react';
import _ from 'lodash';
import API_Service, { axiosDefaultConfig } from './API_Service';
import { req_get_preference, req_save_preference } from '../Helper/Constants';
import { de } from '../Helper/Helper';

export default function useColumnPreference(pageKey, defaultColumns, client) {
	const [columns, setColumns] = useState([]);
	const [isPreferenceLoaded, setIsPreferenceLoaded] = useState(false);
	const savedPrefRef = useRef(null);
	const columnsRef = useRef([]);
	const debounceRef = useRef(null);

	const applyPreference = useCallback((cols, pref) => {
		if (!pref || _.isEmpty(pref)) return cols.map(col => _.merge({}, col));
		return cols.map(col => {
			const copy = _.merge({}, col);
			if (_.has(pref, col.name)) {
				copy.options = { ...copy.options, display: pref[col.name] };
			}
			return copy;
		});
	}, []);

	// Load saved preferences on mount
	useEffect(() => {
		if (!client || !pageKey) return;
		API_Service.get(
			req_get_preference + '?page_key=' + encodeURIComponent(pageKey) + '&preference_type=column_visibility',
			axiosDefaultConfig(client)
		).then(({ data }) => {
			const { result, extraData } = data;
			if (result === 1 && extraData && extraData.preference_json) {
				try {
					const pref = JSON.parse(extraData.preference_json);
					savedPrefRef.current = pref;
				} catch (e) {
					de('Failed to parse preference JSON');
				}
			}
			setIsPreferenceLoaded(true);
		}).catch(() => {
			setIsPreferenceLoaded(true);
		});
	}, [client, pageKey]);

	// Apply preferences when defaultColumns change or preferences load
	useEffect(() => {
		if (!defaultColumns || defaultColumns.length === 0) return;
		if (!isPreferenceLoaded) return;
		const merged = applyPreference(defaultColumns, savedPrefRef.current);
		columnsRef.current = merged;
		setColumns(merged);
	}, [defaultColumns, isPreferenceLoaded, applyPreference]);

	const saveToServer = useCallback((cols) => {
		if (!client || !pageKey) return;
		const prefMap = {};
		cols.forEach(col => {
			prefMap[col.name] = col.options && col.options.display !== false && col.options.display !== 'false';
		});
		const formdata = new FormData();
		formdata.append('page_key', pageKey);
		formdata.append('preference_type', 'column_visibility');
		formdata.append('preference_json', JSON.stringify(prefMap));
		API_Service.post(req_save_preference, formdata, axiosDefaultConfig(client)).then(({ data }) => {
			if (data.result === 1) {
				savedPrefRef.current = prefMap;
				de('Column preference saved for ' + pageKey);
			}
		}).catch((err) => {
			de('Failed to save column preference: ' + err);
		});
	}, [client, pageKey]);

	// Mutate in place: MUIDataTable already keeps column visibility in its own state,
	// so a new array identity here would force a full table reprocess on every toggle.
	const onColumnViewChange = useCallback((changedColumn, action) => {
		const updated = columnsRef.current;
		const target = updated.find(col => col.name === changedColumn);
		if (!target) return;
		target.options = { ...target.options, display: action === 'add' };
		const prefMap = {};
		updated.forEach(col => {
			prefMap[col.name] = col.options && col.options.display !== false && col.options.display !== 'false';
		});
		savedPrefRef.current = prefMap;
		// Debounced save
		if (debounceRef.current) {
			debounceRef.current.cancel();
		}
		debounceRef.current = _.debounce(() => {
			saveToServer(updated);
		}, 500);
		debounceRef.current();
	}, [saveToServer]);

	return { columns, onColumnViewChange, isPreferenceLoaded };
}
